/**
 * Browser-safe access to the AUDIT-08 metric definitions, extended for CRO metrics.
 *
 * Carried forward from packages/spa/src/lib/definitions.ts.
 * Plan 02 already added the 11 CRO entries (CRO-01..CRO-11) to definitions.json,
 * so this file just re-exports the shared loader with CRO-specific helpers.
 *
 * The Vite alias `@saledash/shared/definitions/schema` resolves to the raw .ts file
 * (see vite.config.ts) so the bundler does NOT drag in the Node-only `loader.ts`.
 */
import {
  DefinitionsFileSchema,
  type Definition,
  type DefinitionsFile,
} from '@saledash/shared/definitions/schema';

// Vite/Rollup transforms JSON imports into the parsed object literal at
// build time — no FS access at runtime, no Node APIs required.
import rawDefinitions from '../../../../.planning/audits/definitions.json';

// Validate at module-load time. If the definitions.json shape changes without
// a corresponding schema update, the CRO SPA fails to mount with a clear
// Zod error — same fail-fast behaviour as the Node loader.
const validated: DefinitionsFile = DefinitionsFileSchema.parse(rawDefinitions);

export function loadDefinitions(): DefinitionsFile {
  return validated;
}

export function getMetricDefinition(id: string): Definition {
  const found = validated.metrics.find((m) => m.metric_id === id);
  if (!found) {
    throw new Error(
      `[saledash/spa-cro] No definition found for metric_id="${id}". ` +
        `Available: ${validated.metrics.map((m) => m.metric_id).join(', ')}`,
    );
  }
  return found;
}

/**
 * Convenience helper for CRO metric definitions.
 * Validates that the metric_id starts with 'CRO-' — a defensive guard
 * so callers don't accidentally pass METRIC-01 IDs into CRO tile rendering.
 */
export function getCroMetricDefinition(id: string): Definition {
  if (!id.startsWith('CRO-')) {
    throw new Error(
      `[saledash/spa-cro] getCroMetricDefinition called with non-CRO metric_id="${id}". ` +
        `Use getMetricDefinition() for non-CRO metrics.`,
    );
  }
  return getMetricDefinition(id);
}

export type { Definition, DefinitionsFile };
